import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginApi } from '../../services/authApi';
import { SessionManager } from '../../utils/SessionManager';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { SnackbarType } from '../../components/Snackbar';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { SignInFormData, SignInProps, UseSignInResult } from './types';
import { User } from '../../types/entities';

// SafariのパスワードマネージャーのためのWindow拡張
declare global {
  interface Window {
    markLoginSuccess?: () => void;
  }
}

/**
 * サインインフォーム用カスタムフック
 */
export const useSignIn = ({ setCurrentUser }: SignInProps): UseSignInResult => {
  // フォームデータ
  const [formData, setFormData] = useState<SignInFormData>({
    email: '',
    password: ''
  });
  
  // ローディング・エラー状態
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  
  // ヘルパーフック
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { handleError } = useErrorHandler();
  
  // Safari向けのパスワード自動保存強化
  useEffect(() => {
    // フォームとフィールドの参照を取得
    const form = document.querySelector('form.sign-in-form');
    const emailInput = document.getElementById('email');
    const passwordInput = document.getElementById('password');
    
    // フォームが送信完了したことをSafariに伝えるフラグ
    let loginSuccessful = false;
    
    if (emailInput && passwordInput) {
      // 最適なSafari対応のため、入力スタイルを調整
      emailInput.style.webkitAppearance = 'textfield';
      passwordInput.style.webkitAppearance = 'textfield';
    }
    
    // ページ離脱時にパスワード保存を促進するためのイベント
    const handleBeforeUnload = () => {
      if (loginSuccessful && form && emailInput && passwordInput) {
        // これがキーポイント：ログイン成功時にフォームを「完了」状態にする
        const tempSubmitButton = document.createElement('input');
        tempSubmitButton.type = 'submit';
        tempSubmitButton.style.display = 'none';
        form.appendChild(tempSubmitButton);
        setTimeout(() => {
          if (form.contains(tempSubmitButton)) {
            form.removeChild(tempSubmitButton);
          }
        }, 100);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // ナビゲーション前のフック関数（ログイン成功時にフラグをセット）
    const markLoginSuccess = () => {
      loginSuccessful = true;
    };
    
    // グローバルイベントとして登録（ログイン成功時に呼び出される）
    window.markLoginSuccess = markLoginSuccess;
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      delete window.markLoginSuccess;
    };
  }, []);

  // 入力変更ハンドラ
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // フォーム送信ハンドラ
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 入力バリデーション
    if (!formData.email || !formData.password) {
      setError('メールアドレスとパスワードを入力してください。');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await loginApi(formData.email, formData.password);
      
      if (response && response.token) {
        // セッションマネージャーにユーザー情報を保存
        SessionManager.addSession(
          response.user.id,
          response.user.role,
          response.token,
          response.user.name,
          response.user.icon_path
        );
        
        // パスワード変更強制フラグをセッションに保存
        SessionManager.setMustChangePassword(!!response.requires_password_change);

        // 利用規約同意が必要な場合のチェック
        if (response.requires_terms_agreement && response.current_terms) {
          // ログイン成功だが利用規約同意が必要
          showSnackbar('利用規約への同意が必要です', SnackbarType.INFO);
        } else if (response.requires_password_change) {
          showSnackbar('パスワードを変更してください', SnackbarType.INFO);
        } else {
          // 通常のログイン成功
          showSnackbar('ログインしました', SnackbarType.SUCCESS);
        }
        
        // 親コンポーネントの状態を更新（User型に適合する形に変換）
        const userForState: User = {
          id: Number(response.user.id),
          email: formData.email, // ログインフォームから取得
          name: response.user.name,
          role: response.user.role,
          icon_path: response.user.icon_path ?? undefined
        };
        setCurrentUser(userForState);
        
        // Safariのパスワード保存のために成功フラグをセット
        if (typeof window.markLoginSuccess === 'function') {
          window.markLoginSuccess();
        }
        
        // ホームページへ遷移（利用規約チェックはTermsAgreementGuardで行われる）
        navigate('/');
      } else {
        setError('認証に失敗しました。');
      }
    } catch (err: unknown) {
      const { message } = handleError(err, {
        component: 'SignInForm',
        action: 'login',
        defaultMessage: 'メールアドレスまたはパスワードが正しくありません。',
        extra: {
          email: formData.email,
          hasPassword: !!formData.password
        }
      });
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    isLoading,
    error,
    handleInputChange,
    handleSubmit
  };
}; 
